import React, { useState, useRef, useEffect, Fragment, useMemo } from "react";
import { ExpandMore, GetApp, ChevronRight, UnfoldMore } from "@material-ui/icons";
import _ from "lodash";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getExpandedRowModel,
  getSortedRowModel,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import TableLoadingSkeleton from "../TableLoadingSkeleton";

export default function DesignationsTable(props) {
  const tableContainerRef = useRef();
  const [sorting, setSorting] = useState([]);
  const columnHelper = createColumnHelper();

  const columns = [
    columnHelper.accessor("manufacturer_name", {
      header: "Mfr",
      enableSorting: false,
      cell: ({ renderValue }) => <div className="t--sm t--500">{renderValue()}</div>,
    }),
    columnHelper.accessor((row) => row.entity, {
      header: "Entity",
      enableSorting: false,
      cell: ({ row, getValue }) => {
        return <div className="t--sm t--500">{getValue().id_340b}</div>;
      },
    }),
    columnHelper.accessor((row) => row.pharmacy, {
      header: "Pharmacy",
      enableSorting: false,
      cell: ({ row, getValue }) => {
        return (
          <div>
            <div className="t--sm t--500">{getValue().pharmacy_name}</div>
            <div className="t--sm t--subtitle">{getValue().address}</div>
          </div>
        );
      },
    }),
    columnHelper.accessor("health_industry_number", {
      enableSorting: false,
      header: "HIN",
      cell: ({ renderValue }) => <div className="t--sm t--500">{renderValue()}</div>,
    }),
    columnHelper.accessor("kind", {
      header: "Type",
      enableSorting: false,
      cell: ({ renderValue }) => <div className="t--sm t--500">{renderValue()}</div>,
    }),
  ];

  const claims = useMemo(() => props.claims, [props.claims]);

  const table = useReactTable({
    data: claims,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getExpandedRowModel: getExpandedRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const { rows } = table.getRowModel();

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => 50,
    overscan: 10,
  });

  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();

  const renderVirtualRows = () => {
    return virtualRows.map((virtualRow) => {
      const row = rows[virtualRow.index];

      return (
        <tr key={row.id} className="sm" ref={rowVirtualizer.measureElement} data-index={virtualRow.index}>
          {row.getVisibleCells().map((cell) => {
            return (
              <td key={cell.id} className="t--sm">
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            );
          })}
        </tr>
      );
    });
  };

  const paddingTop = virtualRows.length > 0 ? virtualRows?.[0]?.start || 0 : 0;
  const paddingBottom = virtualRows.length > 0 ? totalSize - (virtualRows?.[virtualRows.length - 1]?.end || 0) : 0;

  return (
    <>
      <div ref={tableContainerRef} className="virtual__table__reports__container">
        <table className="primary">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="sm">
                {headerGroup.headers.map((header) => (
                  <th key={header.id}>
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    {header.column.getCanSort() && (
                      <span
                        style={{ fontSize: 12, display: "inline-flex", verticalAlign: "top" }}
                        onClick={() => header.column.toggleSorting()}
                      >
                        <UnfoldMore fontSize="inherit" color="inherit" />
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {paddingTop > 0 && (
              <tr>
                <td colSpan={columns.length} style={{ height: `${paddingTop}px` }} />
              </tr>
            )}
            {renderVirtualRows()}
            {paddingBottom > 0 && (
              <tr>
                <td colSpan={columns.length} style={{ height: `${paddingBottom}px` }} />
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
